import { useCallback, useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { usePatientDetailQuery } from '@/features/patients/hooks/queries/usePatientsQuery'
import { usePatientActionsQuery } from '@/features/patient-actions/hooks/queries/usePatientActionsQuery'
import { usePostSoapProcessingQuery } from '@/features/patient-actions/hooks/queries/usePostSoapProcessingQuery'
import { useToast } from '@/shared/contexts/ToastContext'
import { Patient } from '@/features/patients/types/patients'
import {
  ActionStatus,
  Action,
} from '@/features/patient-actions/types/patientActions'
import { YakurekiDetail } from '@/shared/types/yakurekiCore'
import { useUpdateActionStatusMutation } from '@/features/patient-actions/hooks/mutations/useUpdateActionStatusMutation'
import { queryKeys } from '@/shared/hooks/queries/queryKeys'

interface UsePatientManagementReturn {
  /**
   * 表示用患者情報（API取得失敗時はyakurekiDetailからフォールバック）
   */
  displayPatient: Patient

  /**
   * 患者API取得エラー
   */
  patientError: Error | null

  /**
   * アクション提案リスト
   */
  actionProposals: Action[]

  /**
   * アクションAPI取得エラー
   */
  actionsError: Error | null

  /**
   * アクションステータス更新ハンドラ
   * - ミューテーション実行後、自動的にアクションリストをリフレッシュ
   * - 成功/失敗時にトースト表示
   */
  handleActionStatusUpdate: (
    actionId: string,
    newStatus: ActionStatus
  ) => Promise<void>
}

/**
 * 患者管理関連のロジックを集約したカスタムフック
 *
 * 責務:
 * - 患者詳細情報の取得とフォールバック
 * - アクション提案リストの取得
 * - アクションステータス更新（ミューテーション）
 * - SOAP生成後の在宅介入判定結果による自動リフレッシュ
 *
 * 使用箇所: PostRecordingConfirmationScreen
 *
 * 内部動作:
 * - usePostSoapProcessingQuery で在宅介入判定をポーリング
 * - 判定結果取得時に自動的にアクションリストを再取得
 */
export const usePatientManagement = (
  yakurekiDetail: YakurekiDetail | null
): UsePatientManagementReturn => {
  const { showToast } = useToast()
  const queryClient = useQueryClient()

  // TanStack Query Mutation
  const updateActionStatusMutation = useUpdateActionStatusMutation()

  // 患者詳細情報をAPIから取得
  const { data: patient, error: patientError } = usePatientDetailQuery(
    yakurekiDetail?.patient_id || null
  )

  // 患者のアクションデータを取得
  const { data: actionsData, error: actionsError } = usePatientActionsQuery(
    yakurekiDetail?.patient_id || null
  )
  const actionProposals = actionsData ?? []

  // SOAP生成後の非同期処理を監視（在宅介入判定）
  const { homeCareAction } = usePostSoapProcessingQuery(yakurekiDetail?.id)

  // 在宅介入判定の結果が取得された時にアクションリストを自動更新
  // invalidateQueries により TanStack Query が自動的に再フェッチを行う
  useEffect(() => {
    if (homeCareAction && yakurekiDetail?.patient_id) {
      queryClient.invalidateQueries({
        queryKey: queryKeys.patientActions.forPatient(
          yakurekiDetail.patient_id
        ),
      })
    }
  }, [homeCareAction, yakurekiDetail?.patient_id, queryClient])

  // 患者情報のフォールバック（API取得失敗時用）
  const displayPatient: Patient = patient || {
    id: yakurekiDetail?.patient_id || 'unknown',
    patient_code: 'N/A',
    name_kanji: yakurekiDetail?.label || '不明な患者',
    name_kana: '',
    last_visit_date: yakurekiDetail?.created_at?.split('T')[0] || '',
  }

  // アクションのステータスを更新
  const handleActionStatusUpdate = useCallback(
    async (actionId: string, newStatus: ActionStatus) => {
      try {
        await updateActionStatusMutation.mutateAsync({
          actionId,
          status: newStatus,
        })

        // ステータス更新後にアクションリストを再取得
        if (yakurekiDetail?.patient_id) {
          await queryClient.invalidateQueries({
            queryKey: queryKeys.patientActions.forPatient(
              yakurekiDetail.patient_id
            ),
          })
        }

        // ステータスに応じたメッセージを表示
        const statusMessage: Record<ActionStatus, string> = {
          pending: 'アクションを保留中にしました',
          planned: 'アクションを計画済みにしました',
          in_progress: 'アクションを実施中にしました',
          done: 'アクションを完了しました',
          declined: 'アクションを却下しました',
        }
        showToast(statusMessage[newStatus], 'success')
      } catch {
        showToast('アクションの更新に失敗しました', 'error')
      }
    },
    [yakurekiDetail, queryClient, showToast, updateActionStatusMutation]
  )

  return {
    displayPatient,
    patientError,
    actionProposals,
    actionsError,
    handleActionStatusUpdate,
  }
}
